/**
 * Job hooks.
 *
 * A running job is polled; once every job is terminal the polling stops, so an
 * idle Ingest page makes no requests. Finishing a job invalidates the judgment
 * caches, which is what makes new records appear in search without a reload.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { api } from '@/lib/apiClient'
import { queryKeys } from '@/lib/queryKeys'
import type {
  BrowserSearchRequest,
  CurlCaptureRequest,
  Job,
  JobStatus,
} from '@/types/api'

const ACTIVE_STATUSES: readonly JobStatus[] = ['pending', 'running']

export function isJobActive(job: Job): boolean {
  return ACTIVE_STATUSES.includes(job.status)
}

export function useJobs(limit = 50) {
  const queryClient = useQueryClient()
  const previousActive = useRef(0)

  const query = useQuery({
    queryKey: queryKeys.jobs.list(),
    queryFn: () => api.listJobs(limit),
    // Poll only while something is in flight.
    refetchInterval: (result) => {
      const jobs = result.state.data
      return jobs?.some(isJobActive) ? 2000 : false
    },
  })

  // When the active count falls, a job just finished: refresh everything it
  // could have changed.
  const activeCount = query.data?.filter(isJobActive).length ?? 0
  useEffect(() => {
    if (previousActive.current > 0 && activeCount < previousActive.current) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.judgments.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.captures.all })
    }
    previousActive.current = activeCount
  }, [activeCount, queryClient])

  return query
}

export function useCaptures(limit = 50) {
  return useQuery({
    queryKey: queryKeys.captures.list(),
    queryFn: () => api.listCaptures(limit),
    staleTime: 30_000,
  })
}

/**
 * One page of captures.
 *
 * `placeholderData` keeps the previous page on screen while the next one
 * loads, so paging does not blank the section and shift the layout under the
 * pointer — the list is polled, and a blank frame on every poll would be worse
 * than the wait.
 */
export function useCapturePage(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: queryKeys.captures.page(page, pageSize),
    queryFn: () => api.listCapturePage(page, pageSize),
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  })
}

export function useImportableFiles() {
  return useQuery({
    queryKey: queryKeys.jobs.importable(),
    queryFn: () => api.listImportable(),
    staleTime: 30_000,
  })
}

/** Shared success handling for every job-creating mutation. */
function useJobMutationOptions() {
  const queryClient = useQueryClient()
  return {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  }
}

export function useCreateJsonImport() {
  const options = useJobMutationOptions()
  return useMutation({
    mutationFn: (paths: string[]) => api.createJsonImport(paths),
    ...options,
  })
}

export function useCreateCurlCapture() {
  const options = useJobMutationOptions()
  return useMutation({
    mutationFn: (body: CurlCaptureRequest) => api.createCurlCapture(body),
    ...options,
  })
}

export function useCreateBrowserSearch() {
  const options = useJobMutationOptions()
  return useMutation({
    mutationFn: (body: BrowserSearchRequest) => api.createBrowserSearch(body),
    ...options,
  })
}

/**
 * Ingestion routes this build of the engine supports.
 *
 * The engine is developed alongside this UI and a source can be removed there,
 * so the tabs are driven by what the server reports rather than hard-coded.
 * Cached for the session: it cannot change without a server restart.
 */
export function useCapabilities() {
  return useQuery({
    queryKey: ['job-capabilities'],
    queryFn: () => api.capabilities(),
    staleTime: Infinity,
  })
}

/** The columns an uploaded file may contain. Static for the session. */
export function useImportSchema() {
  return useQuery({
    queryKey: ['import-schema'],
    queryFn: () => api.importSchema(),
    staleTime: Infinity,
  })
}

/**
 * Upload a file, either to validate it or to import it.
 *
 * A dry run must not disturb the caches (nothing was stored), so only a real
 * import invalidates.
 */
export function useImportFile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      file,
      label,
      dryRun,
    }: {
      file: File
      label?: string
      dryRun?: boolean
    }) => api.importFile(file, { label, dryRun }),
    onSuccess: (_result, variables) => {
      if (variables.dryRun) return
      void queryClient.invalidateQueries({ queryKey: ['jobs'] })
      void queryClient.invalidateQueries({ queryKey: ['captures'] })
      void queryClient.invalidateQueries({ queryKey: ['judgments'] })
      void queryClient.invalidateQueries({ queryKey: ['judgment-stats'] })
      void queryClient.invalidateQueries({ queryKey: ['judgment-facets'] })
    },
  })
}
